import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";


// Redux
import { setCartItem } from "../../../redux/features/cartSlice";
import { useDispatch } from "react-redux";

const QUOTE_QTY_THRESHOLD = 2000;

const BtnAddToCart = ({ ...product }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const moq = product?.multipleOrderQuantity || 1;
  const [quantity, setQuantity] = useState(moq);
  const [currentUnitPrice, setCurrentUnitPrice] = useState(product?.price || 0);
  const [total, setTotal] = useState(0);
  const shouldShowQuote = quantity >= QUOTE_QTY_THRESHOLD;

  /**
   * 根据当前数量计算单价（匹配批量定价阶梯）
   */
  useEffect(() => {
    let price = product?.price || 0;
    
    if (product?.bulk_Pricing && product.bulk_Pricing.length > 0) {
      // 按数量降序排列，找到第一个小于或等于当前数量的阶梯
      const sortedPricing = [...product.bulk_Pricing].sort((a, b) => parseInt(b.Qty) - parseInt(a.Qty));
      const match = sortedPricing.find(p => quantity >= parseInt(p.Qty));
      
      if (match) {
        const matchPriceStr = match.UnitPrice?.toString().replace(/[^0-9.]/g, '') || "0";
        price = parseFloat(matchPriceStr);
      }
    }
    
    setCurrentUnitPrice(price);
    setTotal(quantity * price);
  }, [quantity, product?.price, product?.bulk_Pricing]);

  /**
   * 增加商品数量
   */
  const increseQty = () => {
    setQuantity(prevQty => prevQty + moq);
  };

  /**
   * 减少商品数量
   */
  const decreseQty = () => {
    if (quantity <= moq) return;

    setQuantity(prevQty => prevQty - moq);
  };

  /**
   * 处理键盘输入数量
   * @param {Event} e - 输入事件
   */
  const handleQuantityChange = (e) => {
    let value = parseInt(e.target.value);

    if (isNaN(value) || value < moq) {
      setQuantity(moq);
    } else {
      // 向上取整到最近的倍数
      value = Math.ceil(value / moq) * moq;
      setQuantity(value);
    }
  };

  /**
   * 将商品添加到购物车，并携带商品单重用于后续运费预估。
   */
  const setItemToCart = () => {
    const cartItem = {
      product: product?._id,
      name: product?.name,
      price: currentUnitPrice,
      image: product?.image[0],
      stock: product?.stock,
      multipleOrderQuantity: moq,
      quantity,
      weight: product?.Weight ?? product?.weight,
    };

    dispatch(setCartItem(cartItem));
    toast.success("Item added to cart");
  };

  /**
   * 跳转到询价页面，并带上当前商品和数量
   */
  const goToQuotePage = () => {
    const searchParams = new URLSearchParams();

    if (product?._id) {
      searchParams.set("productId", product._id);
    }

    searchParams.set("qty", `${quantity}`);

    navigate(`/request_quote?${searchParams.toString()}`);
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      {/* 单元价格和数量选择器 */}
      <div className="flex items-center justify-between mb-2 md:mb-4">
        <div className="flex items-center text-xs md:text-sm">
          <span className="text-gray-600 md:text-gray-900 font-medium md:font-bold mr-1">Unit Price</span>
          <span className="text-gray-900 font-bold text-lg md:text-2xl ml-1">
            <span className="font-normal text-sm md:text-lg mr-1">$</span>
            <span>{currentUnitPrice.toFixed(3)}</span>
          </span>
        </div>

        <div className="flex items-center scale-90 md:scale-100 origin-right">
          <button
            onClick={decreseQty}
            type="button"
            className="bg-[#3B82F6] hover:bg-blue-600 text-white w-8 h-8 md:w-9 md:h-9 flex items-center justify-center transition-colors rounded-l-md"
          >
            <span className="text-xl font-bold">−</span>
          </button>
          <input
            type="number"
            step={moq}
            value={quantity}
            onChange={handleQuantityChange}
            className="w-10 h-8 md:w-12 md:h-9 text-center text-sm focus:outline-none border-y border-gray-200 bg-white [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            min={moq}
          />
          <button
            onClick={increseQty}
            type="button"
            className="bg-[#3B82F6] hover:bg-blue-600 text-white w-8 h-8 md:w-9 md:h-9 flex items-center justify-center transition-colors rounded-r-md"
          >
            <span className="text-xl font-bold">+</span>
          </button>
        </div>
      </div>

      {/* 最小起订量和总额 */}
      <div className="flex flex-col gap-2 md:gap-4 mb-4 md:mb-6">
        {moq > 1 && (
          <div className="text-[10px] md:text-sm text-gray-500 italic">
            * Min Order: {moq}(Multiple of {moq})
          </div>
        )}

        <div className="flex items-center mt-1">
          <span className="text-gray-600 md:text-gray-900 font-medium md:font-bold text-xs md:text-sm mr-2">Total:</span>
          <span className="font-bold text-gray-900 text-base md:text-xl ml-1">
            <span className="font-normal text-sm md:text-lg mr-1">$</span>
            <span>{Number(total).toFixed(3)}</span>
          </span>
        </div>
      </div>

      <button
        type="button"
        disabled={!shouldShowQuote && product?.stock === 0}
        className="w-full text-white bg-blue-500 hover:bg-blue-600 focus:outline-none font-bold rounded-lg text-sm md:text-base py-3 md:py-3.5 text-center disabled:bg-gray-300 disabled:cursor-not-allowed transition-all shadow-md active:scale-95"
        onClick={shouldShowQuote ? goToQuotePage : setItemToCart}
      >
        {shouldShowQuote ? "Get a Quote" : "Add To Cart"}
      </button>
    </div>
  );
};

export default BtnAddToCart;
